import React, { useEffect, useMemo } from "react";

/**
 * Single-file, preview-safe article page.
 * - No external imports (prevents "File not found" errors in the editor).
 * - Paste your legacy HTML into `legacyHtml` and it will render inside the site frame.
 * - Strips <head>/<style>/<script>/<link> so it doesn't break your app styling.
 * - Optional image rewrite: /images/... -> /images/articles/...
 */

const BRAND = {
  white: "#ffffff",
  charcoal: "#0b0b0f",
  gray: "#6b7280",
  burgundy: "#800020",
  gold: "#b08d57",
  lilac: "#a78bfa", // sponsor-only
};

function Rule({ className = "" }) {
  return <div className={`h-px w-full bg-black/10 ${className}`} />;
}

function Box({ children, className = "" }) {
  return <div className={`border border-black/15 bg-white ${className}`}>{children}</div>;
}

function SafeIcon() {
  // Preview-safe icon (no asset import). Replace with your real logo icon in your app.
  return (
    <svg width="36" height="36" viewBox="0 0 64 64" aria-hidden="true">
      <rect x="6" y="6" width="52" height="52" fill="#0b0b0f" />
      <rect x="12" y="12" width="40" height="40" fill="#ffffff" />
      <path d="M18 42V22h10c7 0 12 4 12 10s-5 10-12 10H18zm6-5h4c4 0 7-2 7-5s-3-5-7-5h-4v10z" fill="#0b0b0f" />
    </svg>
  );
}

function SiteHeader() {
  return (
    <header className="sticky top-0 z-40 bg-white">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="pt-5 sm:pt-6">
          <div className="flex items-center justify-between gap-3">
            <div className="hidden sm:flex items-center gap-4">
              <a
                href="/"
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                Music × Culture
              </a>
              <a
                href="/"
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                Art × Fashion
              </a>
            </div>
            <div className="flex items-center gap-4">
              <a
                href="/ams"
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                AMS
              </a>
              <a
                href="/login"
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                Login
              </a>
            </div>
          </div>

          <Rule className="mt-4" />

          <div className="grid grid-cols-1 items-end gap-3 py-4 sm:grid-cols-[1fr_auto_1fr]">
            <div className="hidden sm:block">
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">
                Africa Loud Suite
              </div>
              <div className="mt-1 text-xs text-neutral-600">Culture intelligence → creative execution</div>
            </div>

            <a href="/" className="mx-auto flex items-center gap-3 text-center" aria-label="Go to Home">
              <SafeIcon />
              <div>
                <div className="font-serif text-4xl tracking-tight text-neutral-950 sm:text-5xl">Africa Loud</div>
                <div className="-mt-1 text-[11px] font-semibold tracking-[0.25em] uppercase text-neutral-600">
                  Editorial
                </div>
              </div>
            </a>

            <div className="hidden justify-self-end sm:block">
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">Weekly drops</div>
              <div className="mt-1 text-xs text-neutral-600">Signals · Cities · Strategy</div>
            </div>
          </div>

          <Rule />

          <nav className="flex flex-wrap items-center justify-between gap-2 py-2">
            <div className="flex items-center gap-2">
              <a
                href="/"
                className="px-3 py-2 text-[11px] font-semibold tracking-[0.18em] uppercase transition bg-white text-black hover:bg-black/5"
              >
                Home
              </a>
              <a
                href="/"
                className="px-3 py-2 text-[11px] font-semibold tracking-[0.18em] uppercase transition bg-black text-white"
              >
                Culture × Music
              </a>
              <a
                href="/"
                className="px-3 py-2 text-[11px] font-semibold tracking-[0.18em] uppercase transition bg-white text-black hover:bg-black/5"
              >
                Art × Fashion
              </a>
            </div>

            <div className="hidden items-center gap-2 sm:flex">
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">Subscribe</div>
              <div className="h-3 w-px bg-black/15" />
              <div className="text-[11px] text-neutral-600">One email · one week</div>
            </div>
          </nav>

          <Rule />
        </div>
      </div>
    </header>
  );
}

function SiteFooter() {
  const links = [
    { label: "Instagram", href: "https://instagram.com/africaloudmag", external: true },
    { label: "Twitter", href: "https://twitter.com/africaloudmag", external: true },
    { label: "TikTok", href: "https://tiktok.com/@africaloudmag", external: true },
    { label: "Terms", href: "/terms" },
    { label: "Privacy", href: "/terms" },
    { label: "Collab", href: "/collab" },
  ];

  return (
    <footer className="mt-12 bg-neutral-800 px-5 py-6">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm font-semibold text-white">Africa Loud</div>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {links.map((l) => (
              <a
                key={l.label}
                href={l.href}
                target={l.external ? "_blank" : undefined}
                rel={l.external ? "noopener noreferrer" : undefined}
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-white/80 hover:text-white"
              >
                {l.label}
              </a>
            ))}
          </div>
        </div>
        <div className="mt-4 text-xs text-white/70">© 2026 Africa Loud</div>
      </div>
    </footer>
  );
}

function SponsorStrip() {
  return (
    <div className="mt-6">
      <Box className="p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">Partner</div>
            <div className="mt-1 font-serif text-xl tracking-tight text-neutral-950">ON THIRD - Premium Luxury</div>
            <div className="mt-1 text-sm text-neutral-700">Shop the scent with a 10% off offer via the partner link.</div>
          </div>
          <a
            href="https://onthird.co.za/"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center border border-black px-5 py-3 text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-950 hover:bg-black hover:text-white"
            style={{ borderColor: BRAND.lilac }}
          >
            Shop the scent · 10% off
            <span className="ml-2">→</span>
          </a>
        </div>
      </Box>
    </div>
  );
}

function stripLegacyHtml(html) {
  // Remove wrappers/head assets/scripts so legacy pages don’t hijack styling.
  let out = html || "";

  // Remove doctype/html/head/body tags entirely
  out = out
    .replace(/<!doctype[^>]*>/gi, "")
    .replace(/<html[^>]*>/gi, "")
    .replace(/<\/html>/gi, "")
    .replace(/<head[\s\S]*?<\/head>/gi, "")
    .replace(/<body[^>]*>/gi, "")
    .replace(/<\/body>/gi, "");

  // Remove scripts/styles/links (common in pasted legacy pages)
  out = out
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<link[^>]*>/gi, "");

  // Kill on* handlers (onclick, onload, etc.) to avoid runtime errors
  out = out.replace(/\son\w+\s*=\s*"[^"]*"/gi, "");
  out = out.replace(/\son\w+\s*=\s*'[^']*'/gi, "");

  return out.trim();
}

function rewriteLegacyImages(html, prefix = "/images/articles/") {
  // /images/foo.png -> /images/articles/foo.png
  return (html || "").replace(/(src=\s*["'])\/?images\//gi, `$1${prefix}`);
}

function normalizeAnchors(html) {
  // If you have legacy links like /echo/articles/x.html, you can remap them here.
  // Current behavior: keep links as-is.
  return html || "";
}

function ArticlePageTemplate({
  seoTitle,
  seoDescription,
  canonicalPath,
  heroTitle,
  heroByline,
  heroImage,
  legacyHtml,
  stripStyles = true,
  rewriteImages = true,
}) {
  const preparedHtml = useMemo(() => {
    let out = legacyHtml || "";
    if (stripStyles) out = stripLegacyHtml(out);
    if (rewriteImages) out = rewriteLegacyImages(out);
    out = normalizeAnchors(out);
    return out;
  }, [legacyHtml, stripStyles, rewriteImages]);

  useEffect(() => {
    // Minimal SEO in preview: update title/description.
    if (typeof document === "undefined") return;
    if (seoTitle) document.title = seoTitle;

    if (seoDescription) {
      let tag = document.querySelector('meta[name="description"]');
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", "description");
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", seoDescription);
    }

    // Canonical (safe in most apps; in this preview it’s fine too)
    if (canonicalPath) {
      let canon = document.querySelector('link[rel="canonical"]');
      if (!canon) {
        canon = document.createElement("link");
        canon.setAttribute("rel", "canonical");
        document.head.appendChild(canon);
      }
      canon.setAttribute("href", canonicalPath);
    }
  }, [seoTitle, seoDescription, canonicalPath]);

  return (
    <div className="min-h-screen bg-white text-neutral-950">
      <SiteHeader />

      <main className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="pt-8">
          <div className="flex items-center gap-3">
            <div className="h-2 w-2" style={{ backgroundColor: BRAND.burgundy }} />
            <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-neutral-600">Culture × Music</div>
          </div>

          {/* Hero */}
          <div className="mt-6 grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
            <Box className="p-6 sm:p-8">
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">Feature</div>
              <h1 className="mt-4 font-serif text-4xl leading-[1.05] tracking-tight sm:text-6xl">{heroTitle}</h1>
              {heroByline ? <div className="mt-3 text-sm text-neutral-600">{heroByline}</div> : null}
              <div className="mt-6">
                <Rule />
                <div className="mt-3 h-1 w-full" style={{ backgroundColor: BRAND.burgundy }} />
              </div>
            </Box>

            <Box className="relative overflow-hidden">
              <div
                className="h-full min-h-[280px] w-full"
                style={{
                  backgroundImage: `url('${heroImage}')`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />
              <div className="absolute bottom-0 left-0 right-0">
                <div className="bg-white/92 p-5 backdrop-blur-sm">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">Read</div>
                      <div className="mt-1 text-base font-semibold text-neutral-950">Africa Loud</div>
                    </div>
                    <div className="text-[11px] font-semibold tracking-[0.18em] uppercase" style={{ color: BRAND.burgundy }}>
                      Director's pick
                    </div>
                  </div>
                </div>
              </div>
            </Box>
          </div>

          {/* Sponsor stays on all pages */}
          <SponsorStrip />

          {/* Article body */}
          <div className="mt-8 pb-12">
            <Box className="p-6 sm:p-10">
              <article
                className="max-w-none text-neutral-900 leading-relaxed [&_p]:mb-5 [&_h2]:mt-10 [&_h2]:mb-4 [&_h2]:font-serif [&_h2]:text-3xl [&_h2]:tracking-tight [&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:font-serif [&_h3]:text-2xl [&_h3]:tracking-tight [&_ul]:my-5 [&_ul]:pl-6 [&_li]:mb-2 [&_a]:underline [&_a]:decoration-black/30 hover:[&_a]:decoration-black"
                dangerouslySetInnerHTML={{ __html: preparedHtml }}
              />
            </Box>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

// --- “Tests” (runs in dev/preview) ---
function runTemplateSanityTests() {
  // eslint-disable-next-line no-console
  console.assert(stripLegacyHtml("<script>alert(1)</script><p>ok</p>").includes("<p>ok</p>"), "stripLegacyHtml should remove script");
  // eslint-disable-next-line no-console
  console.assert(!stripLegacyHtml("<style>h1{}</style><p>ok</p>").includes("<style"), "stripLegacyHtml should remove style");
  // eslint-disable-next-line no-console
  console.assert(
    rewriteLegacyImages('<img src="/images/a.png" />').includes('/images/articles/a.png'),
    "rewriteLegacyImages should rewrite /images/"
  );
}

export default function BeyonceArticle() {
  useEffect(() => {
    if (typeof window !== "undefined") runTemplateSanityTests();
  }, []);

  return (
    <ArticlePageTemplate
      seoTitle="Beyoncé’s Act I, II, III: Defying Genre Labels · Africa Loud"
      seoDescription="A feature on genre politics and what genreless strategies unlock for African music in global systems."
      canonicalPath="/culture-music/beyonce-genres-global-scene"
      heroTitle="Beyoncé’s Act I, II, III: Defying Genre Labels"
      heroByline="Written by Nolwazi M • Africa Loud Culture Desk"
      // Put your real image in /public/images/articles/act-1-beyonce.png
      heroImage="/images/articles/act-1-beyonce.png"
      stripStyles={true}
      rewriteImages={true}
      legacyHtml={`

  <div class="content">
<img src="/images/act-1-beyonce.png" alt="Beyonce act 1" style="max-width: 100%; height: auto;">
<section>
  <h2>Beyoncé’s Act III: Defying Genre Labels</h2>
  <p>Beyoncé’s anticipated Act III album, the third chapter as she genre shifts following Renaissance (2022) and Cowboy Carter (2024), marks a seismic shift in her career as she defies genre boundaries, most notably challenging the country music label at the 2025 Grammys. Her refusal to be boxed into categories, coupled with her cultural tributes to African music, could inspire a paradigm shift in how African artists like Tyla, Burna Boy, and Wizkid are labelled, moving beyond the restrictive Afrobeats-only tag in international awards. For Africa Loud, this moment is a rallying cry to champion African creativity’s diversity, ensuring artists are recognized for their unique sounds. This feature explores Beyoncé’s genre-defying evolution, her impact on music labelling, the misclassification of African music, and the future of global awards, referencing our <a href="/echo/premium/African_genres.php">African genre map</a> and <a href="/echo/articles/is-tyla-afrobeat.html">“Is Tyla Afrobeats?”</a> article, alongside related works, to advocate for a genreless future for African music.</p>

  <p>Beyoncé’s Act III anticipated album, expected to launch in 2026, builds on her genre-defying legacy, with Cowboy Carter’s 2025 Grammy nominations across Album of the Year, Country Album, and Song of the Year for “Texas Hold ’Em” shattering country music’s gatekeeping. Despite the Country Music Association’s snub, her refusal to conform to the “country” label, blending folk, R&B, and trap with collaborations like Shaboozey and Post Malone, echoes her earlier shifts into house with Renaissance and African influences in The Lion King: The Gift (2019). X posts, like @BeyLegion’s “Beyoncé is genreless,” reflect fan sentiment that Act III may explore new sounds, possibly jazz or rock, as hinted in her “Don’t Hurt Yourself” (2016) with Jack White. This genreless ethos could redefine how African artists are perceived, freeing them from the Afrobeats-only box.</p>

  <p>Beyoncé’s impact at the Grammys, where Cowboy Carter competed across genres, challenges the industry’s tendency to pigeonhole artists. African artists, often confined to Afrobeats categories despite diverse sounds like Amapiano or hiplife, could draw inspiration from her defiance. Tyla’s “Water” (2023), an Amapiano hit mislabelled as Afrobeats at the 2024 MTV VMAs, exemplifies this issue. Beyoncé’s genreless approach, as seen in her African collaborations on The Lion King: The Gift, which debuted at No. 1 on Billboard’s Top R&B Albums chart, could push awards to recognize African music’s spectrum, aligning with Africa Loud’s mission to celebrate unbound creativity.</p>

  <h2>Paying Homage: Beyoncé’s Cultural Tributes</h2>
  <p>Beyoncé’s career is defined by cultural homage, particularly to African influences, which she integrates without genre constraints. Her visual album Lemonade (2016) invoked Yoruba goddess Osun in “Hold Up,” with Beyoncé in a yellow gown mirroring the deity’s imagery, while Nigerian artist Laolu Senbanjo’s Sacred Art of the Ori adorned “Sorry.” Her 2009 co-production of the Broadway musical Fela!, celebrating Fela Kuti’s Afrobeat, inspired an unreleased 20-track Fela-inspired album, per X leaks from @BeyonceVault. These tributes, from “Run the World (Girls)” (2011) with Mozambique’s Tofo Tofo Dance Group’s Pantsula choreography to “End of Time” (2011) echoing Kuti’s rhythms, show her respect for African heritage while defying genre labels.</p>

  <p>This approach contrasts with the industry’s mislabelling of African music, as detailed in our “Is Tyla Afrobeats?” article. Tyla’s pop-piano fusion in “Water,” blending Amapiano’s log drum with R&B and dancehall, was wrongly awarded Best Afrobeats at the 2024 VMAs. Her VMA speech, emphasizing African music’s diversity, sparked X debates, with @tylassecurity calling for Amapiano-specific categories. Beyoncé’s collaborations, like those with Wizkid, Burna Boy, and Busiswa on The Lion King: The Gift, avoid such oversimplifications, showcasing Ghanaian, Nigerian, and South African sounds distinctly, offering a model for awards to follow.</p>

  <h2>The African Genre Map: Decoding Diversity</h2>
  <p>Our <em>African genre map</em>, crafted by Africa Loud, illuminates the rich diversity of African music, often ignored by global awards. Key genres include:</p>
  <ul>
    <li>Afrobeats: A Nigerian-Ghanaian fusion of highlife, juju, hip-hop, and dancehall, popularized by Wizkid’s “Ojuelegba” (2014).</li>
    <li>Amapiano: South Africa’s blend of kwaito, house, and jazz, marked by log drums, as in Tyla’s “Water” (2023).</li>
    <li>Afrobeat: Fela Kuti’s 1970s creation, mixing Yoruba rhythms, jazz, and funk, distinct from Afrobeats, as in Burna Boy’s “Ye” (2018).</li>
    <li>Hiplife: Ghana’s hip-hop and highlife mix, pioneered by Reggie Rockstone, heard in Fuse ODG’s “Azonto” (2012).</li>
    <li>Afro-Adura: A 2022 Afrobeats subgenre with spiritual Yoruba lyrics, featured in Boomplay playlists.</li>
  </ul>
  <p>This map, explored in our interactive feature, highlights distinctions awards overlook, as seen with Tyla’s Amapiano misclassification. X users like @TheKennethDarko criticize this, noting it marginalizes non-Nigerian artists. Beyoncé’s genreless Grammy success with Cowboy Carter, competing beyond country, could inspire awards to adopt our genre map’s nuance, recognizing African music’s full spectrum.</p>

  <h3>The History of African Music’s Global Rise</h3>
  <p>African music’s global ascent began with Fela Kuti’s Afrobeat in the 1970s, a protest sound blending Yoruba rhythms with jazz, as in “Zombie” (1976). The 2000s saw Afrobeats emerge, with 2Baba and D’banj modernizing the sound, followed by Wizkid’s “One Dance” with Drake (2016), hitting No. 1 on the UK Singles Chart. Amapiano rose in the 2010s via SoundCloud, with Kabza De Small and DJ Maphorisa’s tracks sparking global remixes. Tyla’s “Water” (2023) reached No. 7 on the Billboard Hot 100, driven by a 2-billion-view TikTok dance challenge. Spotify reported a 550% Afrobeats stream growth from 2017 to 2022, paralleling K-Pop’s rise in our <a href="/echo/articles/from-kpop-to-afrobeats-amapiano.html">“From K-Pop to Afrobeats/Amapiano”</a> article.</p>

  <p>Beyoncé accelerated this trajectory with The Lion King: The Gift (2019), featuring Burna Boy and Yemi Alade, which Media Online credits for sparking an African music renaissance. Her collaborations, like with Tiwa Savage at the 2023 Coronation of Charles III, amplify African voices. Yet, awards’ Afrobeats bias, as seen in the 2024 Grammy Best African Music Performance nominees (four Nigerian acts), risks overshadowing this diversity, a challenge Beyoncé’s genreless approach could address.</p>

  <h3>Politics and Power: Awards and Cultural Narratives</h3>
  <p>The politics of international awards reveal systemic biases. The 2024 Grammy’s Best African Music Performance category was historic, but its Nigerian-heavy nominees, Burna Boy, Davido, Asake, Olamide, and Tyla, highlight Afrobeats’ dominance, sidelining Amapiano or hiplife. Tyla’s win for “Water” was celebrated, but her VMA “Best Afrobeats” award drew criticism, as she clarified her Amapiano roots on X, echoed by @Olamide0fficial: “Afrobeats as the only African category limits both Afrobeats and Amapiano.” This parallels Tyler, the Creator’s 2020 Grammy critique of “urban” labels, applicable to African music’s categorization.</p>
<img src="/images/act-2-beyonce.jpg" alt="Beyonce act 1" style="max-width: 100%; height: auto;">
  <p>Beyoncé’s Cowboy Carter defied country gatekeeping, earning 2025 Grammy nods across genres, showing how genreless artistry challenges industry norms. African artists, trapped in the Afrobeats label by awards like the VMAs and AMAs, could follow her lead. Our “Is Tyla Afrobeats?” article argues Tyla’s pop-piano fusion demands new categories. Beyoncé’s Act III, potentially blending African rhythms with new genres, could amplify calls for awards reform, as seen in X posts advocating for Amapiano recognition.</p>

  <h3>The Future of International Awards: A Genreless Horizon</h3>
  <p>Beyoncé’s genre-defying Grammy success could reshape international awards, moving beyond Afrobeats-only labels for African artists. The 2024 Grammy’s African category was a start, but its bias, noted by @AfricaFactsZone on X, limits diversity. Proposed reforms include:</p>
  <ul>
    <li>New Categories: Awards, per our <a href="/echo/premium/African_genres.php">genre map</a>, to reflect the distinct sound of the five african regions. </li>
    <li>Diverse Nominees: Include East and Southern African artists like Sauti Sol or Focalistic to balance Nigerian dominance.</li>
    <li>Cultural Education: Consult African music experts, as @TheKennethDarko suggests, to avoid stereotyping.</li>
    <li>Global Platforms: Leverage festivals like AfroNation, to showcase diverse sounds, as Beyoncé did with The Lion King: The Gift.</li>
  </ul>

  <h3>Related Articles and Cultural Context</h3>
  <p>Our analysis builds on Africa Loud’s work and aligns with recent coverage:</p>
  <ul>
    <li><a href="/echo/articles/from-kpop-to-afrobeats-amapiano.html">“From K-Pop to Afrobeats/Amapiano”</a>: Compares BTS’s authenticity with Afrobeats and Amapiano, a model Beyoncé’s genreless approach echoes.</li>
    <li><a href="/echo/articles/is-tyla-afrobeat.html">“Is Tyla Afrobeats?”</a>: Argues Tyla’s pop-piano defies Afrobeats labels, mirroring Beyoncé’s genre defiance.</li>
    <li>OkayAfrica’s “Why Are African Artists Like Tyla Forced to Strip Their Identities?”: Critiques awards’ cultural insensitivity, supporting our reform call.</li>
  </ul>
  <p>These works, with our genre map, underscore the need for awards to reflect African music’s diversity.</p>

  <h3>Conclusion: Redefining Global Recognition</h3>
  <p>For Africa Loud, this is a call to amplify African creativity, ensuring voices from Lagos to Johannesburg are celebrated unbound by genre.</p>
</section>
</article>
        <p class="source">Sources:<br>
        - <em>Rolling Stone</em>: “Chanel Haynes’ First Tour with the Rolling Stones”<br>
        - <em>GRAMMY.com</em>: “2023 In Review: 5 Trends That Defined Afrobeats”<br>
        - <em>Journal of Popular Music Studies</em>: “Gender Wealth Gap in the USA’s Afrobeats Scene”<br>
        - <em>GRAMMY.com</em>: “Meet The Latest Wave Of Rising Afrobeats Stars”<br>
        - <em>NPR</em>: “More breakthroughs, less crossover: Afrobeats is here to stay”<br>
        - <em>African Business</em>: “The unstoppable rise of Afrobeats”<br>
        - <em>OkayAfrica</em>: “Afrobeats or Amapiano - What’s the Best Description for Tyla’s Music?”<br>
        - <em>GRAMMY.com</em>: “10 Afrobeats Songs Turning 10 In 2025”<br>
        - <em>The British Blacklist</em>: “Stop Categorising All African Music As Afrobeats”<br>
        - <em>OkayAfrica</em>: “10 Times Beyoncé’s Work Has Drawn From African Culture”<br>
        - <em>FlytimeFest</em>: “The Afrobeat Evolution”<br>
        - <em>The Gamut</em>: “Music awards reduce African artists to culturally insensitive labels”<br>
        - <em>Bloomberg</em>: “Tyla’s Grammy Win Shows There’s More to African Music Than Afrobeats”<br>
        - Web and X analytics, Spotify data, 2024.<br>
        - Date: June 16, 2025, 9:57 PM SAST
        </p>
    </div>      `}
    />
  );
}
